import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import setup from '../api/setup';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [accessToken, setAccessToken] = useState(null);
    const [user, setUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState(null);

    const setToken = useCallback((token) => {
        setAccessToken(token);
        if (token) {
            localStorage.setItem('access_token', token);
            setup.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
            localStorage.removeItem('access_token');
            delete setup.defaults.headers.common.Authorization;
        }
    }, []);

    // Inicijalizacija - učitaj token iz localStorage ako postoji
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (token) {
            setToken(token);
            // Fetch user profile when token is loaded
            setup.post('/auth/profile')
                .then(response => {
                    const userData = response.data.data || response.data;
                    setUser(userData);
                })
                .catch(error => {
                    console.error('Failed to load user profile:', error.response?.data || error.message);
                    // Token might be invalid, clear it
                    localStorage.removeItem('access_token');
                    setToken(null);
                });
        }
        setIsInitialized(true);
    }, [setToken]);

    const login = useCallback(async (email, password) => {
        try {
            setError(null);

            // 1. Login - dobivaš token
            console.log('Slanjem login zahtjev za:', email);
            const res = await setup.post('/auth/login', { email, password });
            const token = res.data.access_token;

            console.log('Token primljen:', token.substring(0, 20) + '...');

            // 2. Spremi token u state i localStorage
            setToken(token);

            // 3. Dobivaš user podatke - POST zahtjev
            console.log('Tražim profile sa token:', token.substring(0, 20) + '...');
            const profileRes = await setup.post('/auth/profile');

            // Prilagodi prema tvom API odgovoru
            const userData = profileRes.data.data || profileRes.data;
            console.log('User podaci primljeni:', userData);

            setUser(userData);

            // Store user_id in localStorage
            if (userData && userData.id) {
                localStorage.setItem('user_id', userData.id);
            }

            return { token, user: userData };
        } catch (error) {
            console.error('Login error:', error.response?.data || error.message);
            setError(error.response?.data?.message || 'Login failed');
            // Očisti token ako je login neuspješan
            setToken(null);
            throw error;
        }
    }, [setToken]);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setError(null);
        localStorage.removeItem('user_id');
    }, [setToken]);

    const contextValue = useMemo(() => ({
        accessToken,
        isInitialized,
        user,
        setUser,
        setToken,
        login,
        logout,
        error
    }), [accessToken, isInitialized, user, setUser, setToken, login, logout, error]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}